import React, { useCallback, useEffect, useState } from "react";
import { Form, Button, Row, Col, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Image } from "antd";
import {
  fetchCoachProfile,
  updateCoachProfile,
  resetProfileState,
} from "../../redux/Slices/profileSettingsSlice";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import PersonCircle from "../../styles/Assets/person-circle.svg";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import getFileAsBase64 from "../../utils/ImageUtils";
import { useMsal } from "@azure/msal-react";
import useFetchCoachProfile from "../../utils/useFetchCoachProfile";
import config from "../../config/config";

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  useFetchCoachProfile();

  const { instance } = useMsal();

  const { profileData, isLoading, error, updateSuccess, updateError } =
    useSelector((state) => state.profile);
  const [toastConfig, setToastConfig] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    coachId: "",
    profilePictureUrl: "",
    logoImageUrl: "",
    displayName: "",
    firstName: "",
    lastName: "",
    email: "",
    status: "",
    age: 0,
    doj: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipCode: 0,
    profilePictureBase64: "",
    profileFileName: "",
    profileMediaType: "",
    profileFileType: "",
    profileSourceType: "profile",
    logoImageBase64: "",
    logoFileName: "",
    logoMediaType: "",
    logoFileType: "",
    logoSourceType: "profile",
  });

  // Error state for form validation
  const [errors, setErrors] = useState({
    displayName: "",
    firstName: "",
    lastName: "",
    email: "",
  });
  const coachId = profileData?.coachId;

  // Fetch profile on component mount
  useEffect(() => {
    if (coachId) {
      dispatch(fetchCoachProfile({ coachId }));
    }
  }, [dispatch, coachId]);

  useEffect(() => {
    if (profileData) {
      setFormData({
        coachId: coachId,
        displayName: profileData?.displayName || "",
        firstName: profileData?.firstName || "",
        lastName: profileData?.lastName || "",
        email: profileData?.email || "",
        status: profileData?.status || "",
        age: profileData?.age || 0,
        doj: profileData?.doj || "",
        phoneNumber: profileData?.phoneNumber || "",
        address: profileData?.address || "",
        city: profileData?.city || "",
        state: profileData?.state || "",
        country: profileData?.country || "",
        zipCode: profileData?.zipCode || 0,
        profilePictureUrl: profileData?.profilePicture?.url || "",
        profilePictureBase64: "",
        profileFileName: profileData?.profilePicture?.fileName || "",
        profileMediaType: profileData?.profilePicture?.mediaType || "",
        profileFileType: profileData?.profilePicture?.fileType || "",
        profileSourceType: "profile",
        logoImageUrl: profileData?.logoImage?.url || "",
        logoImageBase64: "",
        logoFileName: profileData?.logoImage?.fileName || "",
        logoMediaType: profileData?.logoImage?.mediaType || "",
        logoFileType: profileData?.logoImage?.fileType || "",
        logoSourceType: "profile",
      });
    }
  }, [profileData, coachId]);

  useEffect(() => {
    const updateBase64ForImages = async () => {
      // Fetch profile picture if there's a URL
      if (formData.profilePictureUrl) {
        try {
          const base64Image = await getFileAsBase64(formData.profilePictureUrl);
          if (base64Image) {
            setFormData((prevData) => ({
              ...prevData,
              profilePictureBase64: base64Image,
            }));
          }
        } catch (error) {
          console.error("Error converting profile picture to base64:", error);
        }
      }

      // Fetch logo image if there's a URL
      if (formData.logoImageUrl) {
        try {
          const base64Image = await getFileAsBase64(formData.logoImageUrl);

          if (base64Image) {
            setFormData((prevData) => ({
              ...prevData,
              logoImageBase64: base64Image,
            }));
          }
        } catch (error) {
          console.error("Error converting logo image to base64:", error);
        }
      }
    };

    updateBase64ForImages();
  }, [formData.profilePictureUrl, formData.logoImageUrl, profileData]);
  useEffect(() => {
    const updateBase64ForImages = async () => {
      // Fetch logo image if there's a URL
      if (formData.logoImageUrl) {
        try {
          const base64Image = await getFileAsBase64(formData.logoImageUrl);

          if (base64Image) {
            setFormData((prevData) => ({
              ...prevData,
              logoImageBase64: base64Image,
            }));
          }
        } catch (error) {
          console.error("Error converting logo image to base64:", error);
        }
      }
    };

    updateBase64ForImages();
  }, [formData.logoImageUrl]);

  // Handle input changes for form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle image selection (convert to Base64 and extract fileType, mediaType)
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        let base64Image = reader.result;

        // Check if the base64 image starts with 'data:'
        if (base64Image && base64Image.startsWith("data:")) {
          base64Image = base64Image.split(",")[1];
        }

        const fileType = file.type;
        const mediaType = fileType.split("/")[0];

        // Update the formData based on the type (profilePicture or profileLogo)
        if (type === "profilePicture") {
          setFormData((prevData) => ({
            ...prevData,
            profileFileName: file.name,
            profileMediaType: mediaType,
            profileFileType: fileType,
            profilePictureBase64: base64Image,
          }));
        } else if (type === "profileLogo") {
          setFormData((prevData) => ({
            ...prevData,
            logoFileName: file.name,
            logoMediaType: mediaType,
            logoFileType: fileType,
            logoImageBase64: base64Image,
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle image remove
  const handleImageRemove = (type) => {
    if (type === "profilePicture") {
      setFormData((prevData) => ({
        ...prevData,
        profilePictureUrl: null,
        profilePictureBase64: null,
      }));
    } else if (type === "profileLogo") {
      setFormData((prevData) => ({
        ...prevData,
        logoImageUrl: null,
        logoImageBase64: null,
      }));
    }
  };

  // Reset file input to force re-trigger on re-selection of the same file
  const handleFileInputReset = (id) => {
    const fileInput = document.getElementById(id);
    fileInput.value = "";
  };

  const validateForm = () => {
    let validationErrors = {};
    let isValid = true;

    // Validate display name
    if (!formData.displayName.trim()) {
      validationErrors.displayName =
        trans("Profile.display") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequired");
      isValid = false;
    } else if (formData.displayName.trim().length < 3) {
      // Corrected condition
      validationErrors.displayName =
        trans("Profile.display") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequiredAndMinLength");
      isValid = false;
    }

    // Validate first name
    if (!formData.firstName.trim()) {
      validationErrors.firstName =
        trans("Profile.first") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequired");
      isValid = false;
    } else if (formData.firstName.trim().length < 3) {
      // Corrected condition
      validationErrors.firstName =
        trans("Profile.first") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequiredAndMinLength");
      isValid = false;
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      validationErrors.lastName =
        trans("Common.last") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequired");
      isValid = false;
    } else if (formData.lastName.trim().length < 3) {
      // Corrected condition
      validationErrors.lastName =
        trans("Common.last") +
        " " +
        trans("Common.name").toLowerCase() +
        " " +
        trans("Common.isRequiredAndMinLength");
      isValid = false;
    }

    // Validate email
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      validationErrors.email = trans("Common.validEmail");
      isValid = false;
    }

    setErrors(validationErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Destructure the needed properties from formData
      const {
        coachId,
        profilePictureBase64,
        profileFileName,
        profileMediaType,
        profileFileType,
        profileSourceType,
        logoImageBase64,
        logoFileName,
        logoMediaType,
        logoFileType,
        logoSourceType,
        displayName,
        firstName,
        lastName,
      } = formData;

      // Create the dataToSubmit object with only the required fields
      const dataToSubmit = {
        coachId,
        profilePictureBase64,
        profileFileName,
        profileMediaType,
        profileFileType,
        profileSourceType,
        logoImageBase64,
        logoFileName,
        logoMediaType,
        logoFileType,
        logoSourceType,
        displayName,
        firstName,
        lastName,
      };

      // Dispatch the action to update the coach profile with the required data
      dispatch(updateCoachProfile(dataToSubmit));
    }
  };

  const resetPassword = useCallback(async () => {
    if (instance) {

      try {
        await instance.loginPopup({
          scopes: ["openid", "profile"],
          authority: config.MSAL_FORGOT_PASSWORD,
        });
      } catch (error) {
        if (error.errorMessage && error.errorMessage.includes("AADB2C90118")) {
          console.warn("Password reset is not supported in the current flow.");
        } else {
          console.error("Password Reset Error:", error);
        }
      }
    }
  }, [instance]);

  const handleSetNewPassword = () => {
    // setShowModal(true);
    resetPassword();
  };

  const handleConfirmPasswordReset = () => {
    setShowModal(false);
  };

  const handleCancelPasswordReset = () => {
    setShowModal(false);
  };

  // Toast notification logic
  useEffect(() => {
    if (updateSuccess) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: trans("Profile.profileUpdatedSuccessfully"),
        heading: trans("Common.success"),
      });
      setTimeout(() => {
        dispatch(resetProfileState());
      }, 3000);
    }
    if (error && updateError) {
      // Trigger error toast
      setToastConfig({
        type: "error",
        message: trans("Profile.profileUpdationFailed"),
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        dispatch(resetProfileState());
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess, updateError, trans, error]);

  return (
    <div className="  formContainer">
      {isLoading ? (
        <Loader />
      ) : (
        <Form className="templateWrapper px-4 py-4 m-3" onSubmit={handleSubmit}>
          <Row className="pb-4">
            <Col>
              <h4 className="pb-2 text-color">{trans("Common.profile")}</h4>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button className="saveButton" type="submit">
                <i className="bi bi-floppy2-fill mr-2"></i>
                {trans("Common.save")}
              </Button>
            </Col>
          </Row>

          <Row className="mb-4 align-items-center">
            <Col xs="auto">
              <div className="position-relative profile-image-container">
                {formData.profilePictureBase64 ? (
                  <Image
                    src={`data:${formData.fileType};base64,${formData.profilePictureBase64}`}
                    alt="Profile Picture"
                    className="w-100 h-100 rounded-3"
                    width={100}
                    height={100}
                  />
                ) : (
                  <Image
                    src={PersonCircle}
                    alt="Default Profile"
                    className="w-100 h-100 rounded-3 p-1"
                    width={100}
                    height={100}
                    preview={false}
                  />
                )}
                {formData.profilePictureBase64 && (
                  <Button
                    variant="link"
                    className="position-absolute m-2 p-0 icon-position"
                    onClick={() => handleImageRemove("profilePicture")}
                  >
                    <i
                      className="bi bi-x-circle-fill"
                      style={{ fontSize: "30px", color: "red" }}
                    ></i>
                  </Button>
                )}
                {!formData.profilePictureBase64 && (
                  <Button
                    variant="link"
                    className="position-absolute m-2 p-0 icon-position"
                    onClick={() =>
                      document.getElementById("profilePictureInput").click()
                    }
                  >
                    <i
                      className="bi bi-plus-circle-fill"
                      style={{ fontSize: "30px", color: "green" }}
                    ></i>
                  </Button>
                )}
              </div>
            </Col>
            <Col>
              <h5 className="pb-0 mb-0">
                {trans("Profile.yourProfilePictureTitle")}
              </h5>
              <p className="text-muted lh-lg">
                {trans("Profile.yourProfilePictureSubTitleFirstPara")}
                <br />
                {trans("Profile.yourProfilePictureSubTitleSecoundPara")}
              </p>
              <input
                type="file"
                accept="image/*"
                id="profilePictureInput"
                className="mt-2"
                onChange={(e) => handleImageChange(e, "profilePicture")}
                style={{ display: "none" }}
                onClick={() => handleFileInputReset("profilePictureInput")}
              />
            </Col>
          </Row>

          {/* Profile Logo */}
          <Row className="mb-4 align-items-center">
            <Col xs="auto">
              <div className="position-relative profile-image-container">
                {formData.logoImageBase64 ? (
                  <Image
                    src={`data:${formData.fileType};base64,${formData.logoImageBase64}`}
                    alt="Profile Logo"
                    className="w-100 h-100 rounded-3"
                    width={100}
                    height={100}
                  />
                ) : (
                  <Image
                    src={PersonCircle}
                    alt="Default Logo"
                    className="w-100 h-100 rounded-3 p-1"
                    width={100}
                    height={100}
                    preview={false}
                  />
                )}
                {formData.logoImageBase64 && (
                  <Button
                    variant="link"
                    className="position-absolute m-2 p-0 icon-position"
                    onClick={() => handleImageRemove("profileLogo")}
                  >
                    <i
                      className="bi bi-x-circle-fill"
                      style={{ fontSize: "30px", color: "red" }}
                    ></i>
                  </Button>
                )}
                {!formData.logoImageBase64 && (
                  <Button
                    variant="link"
                    className="position-absolute m-2 p-0 icon-position"
                    onClick={() =>
                      document.getElementById("profileLogoInput").click()
                    }
                  >
                    <i
                      className="bi bi-plus-circle-fill"
                      style={{ fontSize: "30px", color: "green" }}
                    ></i>
                  </Button>
                )}
              </div>
            </Col>
            <Col>
              <h5 className="pb-0 mb-0">{trans("Profile.yourLogoTitle")}</h5>
              <p className="text-muted lh-lg">
                {trans("Profile.yourLogoSubTitle")}
              </p>
              <input
                type="file"
                accept="image/*"
                id="profileLogoInput"
                className="mt-2"
                onChange={(e) => handleImageChange(e, "profileLogo")}
                style={{ display: "none" }}
                onClick={() => handleFileInputReset("profileLogoInput")}
              />
            </Col>
          </Row>

          <Row className="pb-3">
            <Col xs={12} md={6} lg={3} className="mt-3">
              <Form.Group controlId="formDisplayName">
                <FormLabel
                  label={trans("Profile.display") + " " + trans("Common.name")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter")}
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  isInvalid={!!errors.displayName}
                  feedback={errors.displayName}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} lg={3} className="mt-3">
              <Form.Group controlId="formFirstName">
                <FormLabel
                  label={trans("Profile.first") + " " + trans("Common.name")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter")}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  isInvalid={!!errors.firstName}
                  feedback={errors.firstName}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} lg={3} className="mt-3">
              <Form.Group controlId="formLastName">
                <FormLabel
                  label={trans("Common.last") + " " + trans("Common.name")}
                  required
                />
                <FormControl
                  type="text"
                  placeholder={trans("Common.enter")}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  isInvalid={!!errors.lastName}
                  feedback={errors.lastName}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6} lg={3} className="mt-3">
              <Form.Group controlId="formEmail">
                <FormLabel label={trans("Common.email")} required />
                <FormControl
                  type="email"
                  placeholder={trans("Common.enter")}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  feedback={errors.email}
                  isDisabled={true}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <Row>
            <h5 className="pb-2 pt-2">
              {trans("Profile.password") + " " + trans("Common.settings")}
            </h5>
          </Row>

          <Row className="pb-2">
            <Col className="d-flex align-items-start">
              <div>
                <h5 className="pb-0 mb-0">
                  {trans("Profile.changeYourPassword")}
                </h5>
                <p className="lh-lg">
                  {trans("Profile.changeYourPasswordLink")}
                </p>
              </div>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button className="saveButton" onClick={handleSetNewPassword}>
                {trans("Profile.setNewPassword")}
              </Button>
            </Col>
          </Row>
          {/* Modal for confirmation */}
          <Modal show={showModal} onHide={handleCancelPasswordReset}>
            <Modal.Header closeButton>
              <Modal.Title>{trans("Profile.resetPassword")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {trans("Profile.resetPasswordConfirmationMessage")}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-primary"
                className="back_button"
                onClick={handleCancelPasswordReset}
              >
                {trans("Common.cancel")}
              </Button>
              <Button
                className="saveButton"
                onClick={handleConfirmPasswordReset}
              >
                {trans("Common.confirm")}
              </Button>
            </Modal.Footer>
          </Modal>
          {toastConfig && (
            <ToastNotification
              type={toastConfig.type}
              message={toastConfig.message}
              heading={toastConfig.heading}
            />
          )}
        </Form>
      )}
    </div>
  );
};

export default ProfileSettings;
